import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import axiosInstance from '@/setup/axios/axiosInstance';

import AppListToolbarGlobal from '@/modules/inventory/allocation/components/AllocationsToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { RootState } from '@/setup/redux/RootReducer';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import MainListModule from '@/__core/layout/module/MainListModule';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';

import {
  allocationStatusFilterOptions,
  createDefaultAllocationForm,
} from '../allocationDefaults';
import { InventoryAllocationFormData } from '../types';
import AllocationForm from './AllocationForm';
import AllocationList, { AllocationListHandle } from './AllocationList';
import { fetchOrdersThunk } from '@/modules/finance/redux/OrderRedux';
import type { OrderAutocompleteItem } from '@/modules/commons/components/OrderAutocomplete';
import { getStores } from '@/modules/inventory/stores/redux/StoreCRUD';
import { getItems } from '@/modules/inventory/items/redux';
import { formatErrorForSnackbar, toApiError } from '@/__core/helpers/Error';
import { AllocationLine } from '../types';

const buildFormMetadata = (formData: InventoryAllocationFormData, allocationId?: string) => ({
  formData,
  allocationId,
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return undefined;
  }
  return companyId;
};

export default function AllocationModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const dispatch = useDispatch();
  const listRef = useRef<AllocationListHandle>(null);
  const pendingEditRef = useRef<{ orderId: string; orderNo: string; customerName: string; items: any[] } | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [saving, setSaving] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const initialFormDataRef = useRef<Record<number, string>>({});
  const snapshotReadyRef = useRef<Set<number>>(new Set());

  const [orderOptions, setOrderOptions] = useState<OrderAutocompleteItem[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderSearchInput, setOrderSearchInput] = useState('');
  const [debouncedOrderSearch, setDebouncedOrderSearch] = useState('');

  const [storeOptions, setStoreOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearchInput, setStoreSearchInput] = useState('');
  const [debouncedStoreSearch, setDebouncedStoreSearch] = useState('');

  const [unitOptions, setUnitOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitSearchInput, setUnitSearchInput] = useState('');
  const [debouncedUnitSearch, setDebouncedUnitSearch] = useState('');

  const [itemOptions, setItemOptions] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemSearchInput, setItemSearchInput] = useState('');
  const [debouncedItemSearch, setDebouncedItemSearch] = useState('');

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const {
    canCreate: canCreateAllocations,
    canUpdate: canUpdateAllocations,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
  } = useModuleToolbarPermissions({
    resource: 'inventory.materialrequest',
    activeCompanyFilter,
    updateTabTypes: ['update-allocation'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách allocation', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateAllocations) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateAllocations]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_allocation_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateAllocations),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-allocation', 'update-allocation'],
    allowMultipleTabTypes: ['new-allocation', 'update-allocation'],
    buildNewTab: (currentTabs) => {
      const pendingEdit = pendingEditRef.current;
      pendingEditRef.current = null;
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;

      if (pendingEdit) {
        const tabTitle = pendingEdit.customerName
          ? `${pendingEdit.orderNo} - ${pendingEdit.customerName}`
          : pendingEdit.orderNo;

        const lines: AllocationLine[] = (pendingEdit.items || []).map((item: any, index: number) => ({
          id: index + 1,
          item_id: item.item_id || '',
          item_name: item.item_name || '',
          item_code: item.item_code || '',
          unit_id: item.uom_id || '',
          unit_name: item.uom_name || '',
          qty: item.allocated_qty || '',
        }));

        return {
          id: Date.now() + Math.floor(Math.random() * 1000),
          title: tabTitle,
          type: 'update-allocation',
          closable: true,
          metadata: {
            companyId: baseCompanyId,
            ...buildFormMetadata(
              { ...createDefaultAllocationForm(baseCompanyId), order_no: pendingEdit.orderId, lines },
              pendingEdit.orderId
            ),
          },
        };
      }

      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `Allocation #${currentTabs.filter((tab) => tab.type === 'new-allocation').length + 1}`,
        type: 'new-allocation',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultAllocationForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryAllocationFormData | undefined;
  const activeAllocationId = activeTabData?.metadata?.allocationId as string | undefined;

  const isDirty = useMemo(() => {
    if (!activeTabData?.id || !activeFormData) return false;
    if (!snapshotReadyRef.current.has(activeTabData.id)) return false;
    const initial = initialFormDataRef.current[activeTabData.id];
    return initial !== JSON.stringify(activeFormData);
  }, [activeFormData, activeTabData?.id]);

  useEffect(() => {
    const tabId = activeTabData?.id;
    if (!tabId || !activeFormData) return;
    if (snapshotReadyRef.current.has(tabId)) return;

    const timer = setTimeout(() => {
      initialFormDataRef.current[tabId] = JSON.stringify(activeFormData);
      const next = new Set(snapshotReadyRef.current);
      next.add(tabId);
      snapshotReadyRef.current = next;
    }, 300);

    return () => clearTimeout(timer);
  }, [activeTabData?.id, activeFormData?.status]);

  useEffect(() => {
    if (activeTabData?.id) {
      handleUnsavedChangesUpdate(isDirty, activeTabData.id);
    }
  }, [isDirty, activeTabData?.id]);

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const updateFormData = (updates: Partial<InventoryAllocationFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;
    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeAllocationId) });
  };

  const lineIdCounterRef = useRef(0);
  const nextLineId = () => {
    lineIdCounterRef.current += 1;
    return lineIdCounterRef.current;
  };

  const handleChangeLine = (lineId: number, updates: Partial<AllocationLine>) => {
    if (!activeTabData?.id || !activeFormData) return;
    const currentLines = activeFormData.lines || [];
    const nextLines = currentLines.map((line) =>
      line.id === lineId ? { ...line, ...updates } : line
    );
    updateFormData({ lines: nextLines });
  };

  const handleAddLine = () => {
    if (!activeTabData?.id || !activeFormData) return;
    const newLine: AllocationLine = {
      id: nextLineId(),
      item_id: '',
      item_name: '',
      item_code: '',
      unit_id: '',
      unit_name: '',
      qty: '',
    };
    updateFormData({ lines: [...(activeFormData.lines || []), newLine] });
  };

  const handleRemoveLine = (lineId: number) => {
    if (!activeTabData?.id || !activeFormData) return;
    const nextLines = (activeFormData.lines || []).filter((line) => line.id !== lineId);
    updateFormData({ lines: nextLines });
  };

  const handleEditAllocation = React.useCallback((orderId: string, orderNo: string, customerName: string, items: any[]) => {
    if (!canUpdateAllocations) return;
    pendingEditRef.current = { orderId, orderNo, customerName, items };
    handleAddTab();
  }, [canUpdateAllocations, handleAddTab]);

  const handleSave = async () => {
    if (!activeFormData || !activeTabData?.id || saving) return;

    const isUpdateMode = Boolean(activeAllocationId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage('Bạn không có quyền cập nhật allocation.');
      setSnackbarSeverity('error');
      return;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage('Bạn không có quyền tạo allocation.');
      setSnackbarSeverity('error');
      return;
    }

    setSaving(true);
    try {
      const lines = (activeFormData.lines || []).filter((l) => l.item_id);

      if (lines.length === 0) {
        setSnackbarSeverity('error');
        setSnackbarMessage('Vui lòng thêm ít nhất một vật tư.');
        setSaving(false);
        return;
      }

      const payload = {
        company_id: activeFormData.company_id,
        store_id: activeFormData.store_name || undefined,
        uom_id: lines[0]?.unit_id || undefined,
        order_id: activeFormData.order_no || undefined,
        item_ids: lines.map((l) => l.item_id),
        allocated_qty: lines[0]?.qty || undefined,
        note: activeFormData.note || undefined,
      };

      if (isUpdateMode) {
        await axiosInstance.put(`/inventory/allocations/bulk/${activeAllocationId}/`, payload);
      } else {
        await axiosInstance.post('/inventory/allocations/bulk/', payload);
      }

      handleUnsavedChangesUpdate(false, activeTabData.id);
      setSnackbarSeverity('success');
      setSnackbarMessage('Đã lưu thông tin allocation.');
      updateTab(activeTabData.id, {
        title: activeFormData.name || activeTabData.title,
        metadata: {
          ...buildFormMetadata(activeFormData, activeAllocationId || (activeTabData.metadata as any)?.allocationId),
          companyId: activeFormData.company_id || listScopeCompanyId || selectedCompanyId,
        },
      });
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage(formatErrorForSnackbar(toApiError(error)));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrderSearch(orderSearchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [orderSearchInput]);

  useEffect(() => {
    let cancelled = false;

    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const result = await dispatch(
          fetchOrdersThunk({
            no_pagination: false,
            page_size: 20,
            search: debouncedOrderSearch || undefined,
            company: activeCompanyFilter,
            company_id: activeCompanyFilter,
            ordering: '-created_at',
          })
        );
        if (!cancelled && fetchOrdersThunk.fulfilled.match(result)) {
          const payload = result.payload;
          const data = Array.isArray(payload) ? payload : payload?.results || [];
          setOrderOptions(data as OrderAutocompleteItem[]);
        }
      } catch {
        if (!cancelled) {
          setOrderOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingOrders(false);
        }
      }
    };

    fetchOrders();
    return () => { cancelled = true; };
  }, [dispatch, activeCompanyFilter, debouncedOrderSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStoreSearch(storeSearchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [storeSearchInput]);

  useEffect(() => {
    let cancelled = false;

    const fetchStores = async () => {
      setLoadingStores(true);
      try {
        const response = await getStores({
          company_id: listScopeCompanyId,
          page_size: 200,
          search: debouncedStoreSearch || undefined,
        });
        if (!cancelled) {
          const data = response?.results || [];
          setStoreOptions(data.map((s: any) => ({ id: s.id, name: s.name })));
        }
      } catch {
        if (!cancelled) {
          setStoreOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingStores(false);
        }
      }
    };

    fetchStores();
    return () => { cancelled = true; };
  }, [listScopeCompanyId, debouncedStoreSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUnitSearch(unitSearchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [unitSearchInput]);

  useEffect(() => {
    let cancelled = false;

    const fetchUnits = async () => {
      setLoadingUnits(true);
      try {
        const response = await axiosInstance.get('/inventory/units/', {
          params: { pagination: 'false', search: debouncedUnitSearch || undefined },
        });
        if (!cancelled) {
          const data = response?.data?.results || response?.data?.data || response?.data;
          const items = Array.isArray(data) ? data : [];
          setUnitOptions(items.map((u: any) => ({ id: String(u.id), name: u.name })));
        }
      } catch {
        if (!cancelled) {
          setUnitOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingUnits(false);
        }
      }
    };

    fetchUnits();
    return () => { cancelled = true; };
  }, [debouncedUnitSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedItemSearch(itemSearchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [itemSearchInput]);

  useEffect(() => {
    let cancelled = false;

    const fetchItems = async () => {
      setLoadingItems(true);
      try {
        const response = await getItems({
          search: debouncedItemSearch || '',
          status: 'all',
          companyId: listScopeCompanyId || '',
          pageSize: 200,
        });
        if (!cancelled) {
          const data = response?.results || [];
          setItemOptions(data.map((i: any) => ({ id: i.id, name: i.name, code: i.code })));
        }
      } catch {
        if (!cancelled) {
          setItemOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingItems(false);
        }
      }
    };

    fetchItems();
    return () => { cancelled = true; };
  }, [listScopeCompanyId, debouncedItemSearch]);

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryAllocationFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box
                  key={tab.id}
                  sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}
                >
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() =>
                              setIsAdvancedSearchOpen((current) => !current)
                            }
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={allocationStatusFilterOptions}
                          />
                        }
                        dataTable={
                          <AllocationList
                            ref={listRef}
                            companyId={listScopeCompanyId}
                            searchQuery={searchQuery}
                            canUpdate={canUpdateAllocations}
                            onEditOrder={handleEditAllocation}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <AllocationForm
                        formData={tabFormData}
                        canEdit={
                          tab.type === 'new-allocation' ? tabPermissions.canCreate : tabPermissions.canUpdate
                        }
                        onChangeForm={(updates) => {
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.allocationId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onInitializeForm={(updates) => {
                          const nextFormData = { ...tabFormData, ...updates };
                          updateTab(tab.id, {
                            metadata: buildFormMetadata(
                              nextFormData,
                              tab.metadata?.allocationId as string | undefined
                            ),
                          });

                          const initialSnapshot = initialFormDataRef.current[tab.id];
                          if (initialSnapshot) {
                            initialFormDataRef.current[tab.id] = JSON.stringify({
                              ...JSON.parse(initialSnapshot),
                              ...updates,
                            });
                          }
                        }}
                        onSave={handleSave}
                        saving={saving}
                        isDirty={isDirty}
                        onChangeLine={handleChangeLine}
                        onAddLine={handleAddLine}
                        onRemoveLine={handleRemoveLine}
                        orderOptions={orderOptions}
                        loadingOrders={loadingOrders}
                        orderSearchInput={orderSearchInput}
                        onOrderSearchChange={setOrderSearchInput}
                        storeOptions={storeOptions}
                        loadingStores={loadingStores}
                        storeSearchInput={storeSearchInput}
                        onStoreSearchChange={setStoreSearchInput}
                        unitOptions={unitOptions}
                        loadingUnits={loadingUnits}
                        itemOptions={itemOptions}
                        loadingItems={loadingItems}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3500}
        onClose={() => setSnackbarMessage(null)}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
